import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Box, Typography } from '@mui/material';
import { FormikValues, useFormikContext } from 'formik';
import { useState, useEffect, useContext } from 'react';
import { getFormattedAge } from '@gv/triage-components/dist/utils/date';
import { Loader, ThemeContext, StandardButton } from '@gv/triage-components';

import { formatDateTime } from 'utils/config';
import { useListQuery as usebitwerxPetDetailAPIQuery } from 'store/api/bitwerx-pet-detail';

import { Value, Heading } from './pims-styles';
export const PimsInfo = ({ client, bitwerxStatus, vetsPireStatus }: any) => {
  const { values } = useFormikContext<FormikValues>();
  const [selectedPetId, setSelectedPetId] = useState<number | null>(null);
  const [petName, setPetName] = useState<string | null>('');
  useEffect(() => {
    const filteredPets = (client?.pets || []).filter((pet: any) => {
      if (bitwerxStatus) {
        return (
          pet.bitwerx_patient_id &&
          pet.bitwerx_patient_id !== null &&
          pet.bitwerx_client_id !== null
        );
      }
      return (
        pet.vetspire_patient_id &&
        pet.vetspire_patient_id !== null &&
        pet.vetspire_client_id !== null
      );
    });
    if (filteredPets.length > 0) {
      setPetName(filteredPets[0].name);
      if (bitwerxStatus) {
        setSelectedPetId(filteredPets[0].bitwerx_patient_id);
      } else if (vetsPireStatus) {
        setSelectedPetId(filteredPets[0].vetspire_patient_id);
      }
    } else {
      setSelectedPetId(null);
      setPetName('');
    }
  }, [client?.pets]);

  const { isLoading, data: data }: any = usebitwerxPetDetailAPIQuery({
    id: selectedPetId,
    hospitalId: client?.hospital_id,
    clientId: client?.bitwerx_client_id || client?.vetspire_client_id,
  });
  const { isDarkTheme } = useContext(ThemeContext);
  const formPetIds = values.pets || [];
  const pets =
    client?.pets?.filter((pet: any) => {
      const isInFormValues = formPetIds.includes(pet.id);
      const matchesStatus =
        (bitwerxStatus &&
          pet.bitwerx_patient_id &&
          pet.bitwerx_patient_id !== null) ||
        (vetsPireStatus &&
          pet.vetspire_patient_id &&
          pet.vetspire_patient_id !== null);
      return isInFormValues && matchesStatus;
    }) || [];

  // Check if selectedPetId exists in values.pets and set to null if it doesn't
  useEffect(() => {
    if (selectedPetId) {
      const petExistsInValues = client?.pets?.some((pet: any) => {
        const matchesSelectedPetId =
          (bitwerxStatus && pet.bitwerx_patient_id === selectedPetId) ||
          (vetsPireStatus && pet.vetspire_patient_id === selectedPetId);
        return matchesSelectedPetId && formPetIds.includes(pet.id);
      });

      if (!petExistsInValues) {
        const firstPet = pets?.[0];
        if (firstPet) {
          setPetName(firstPet.name || '');
          if (bitwerxStatus) {
            setSelectedPetId(firstPet.bitwerx_patient_id);
          } else if (vetsPireStatus) {
            setSelectedPetId(firstPet.vetspire_patient_id);
          }
        }
      } else {
        // Update petName when selectedPetId changes
        const selectedPet = client?.pets?.find((pet: any) => {
          const matchesSelectedPetId =
            (bitwerxStatus && pet.bitwerx_patient_id === selectedPetId) ||
            (vetsPireStatus && pet.vetspire_patient_id === selectedPetId);
          return matchesSelectedPetId && formPetIds.includes(pet.id);
        });
        if (selectedPet && selectedPet.name !== petName) {
          setPetName(selectedPet.name);
        }
      }
    } else if (pets.length > 0) {
      // If no selectedPetId, select the first pet
      const firstPet = pets[0];
      setPetName(firstPet.name || '');
      if (bitwerxStatus) {
        setSelectedPetId(firstPet.bitwerx_patient_id);
      } else if (vetsPireStatus) {
        setSelectedPetId(firstPet.vetspire_patient_id);
      }
    }
  }, [
    selectedPetId,
    formPetIds,
    client?.pets,
    bitwerxStatus,
    vetsPireStatus,
    pets,
  ]);

  const petsScrollable = pets.length > 1;

  if (isLoading && selectedPetId) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: {
            xs: 'auto',
            md: '850px',
            lg: !petsScrollable ? '600px' : '700px',
          },
        }}
      >
        <Loader isFullScreen={false} isLoading={isLoading} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: {
          xs: 'auto',
          md: '850px',
          lg: !petsScrollable ? '600px' : '700px',
        },
      }}
    >
      <Grid container spacing={2}>
        <Grid item md={8} xs={12} style={{ height: '100%' }}>
          <Box sx={{ height: '100%', overflowY: 'visible' }}>
            <Paper
              elevation={1}
              sx={{
                padding: 2,
                width: '100%',
                boxShadow: 'none',
                borderRadius: '20px !important',
                backgroundColor: isDarkTheme ? 'rgb(41,41,41)' : '#F3F6FC',
                border: isDarkTheme
                  ? '1px solid rgb(41,41,41)'
                  : '1px solid #F3F6FC',
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, pl: 1, color: isDarkTheme ? '#ffffff' : '' }}
              >
                Client Information
              </Typography>

              <Paper
                elevation={1}
                sx={{
                  padding: 2,
                  width: '100%',
                  boxShadow: 'none',
                  border: '1px #e6e6e6',
                  borderRadius: '20px !important',
                  backgroundColor: isDarkTheme ? '#3C3C3C' : '#ffffff',
                }}
              >
                <Grid
                  container
                  spacing={2}
                  sx={{ color: isDarkTheme ? '#ffffff' : '' }}
                >
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading>First Name</Heading>
                    <Value>{client?.first_name ?? '--'}</Value>
                  </Grid>
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Last Name</Heading>
                    <Value>{client?.last_name ?? '--'}</Value>
                  </Grid>
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Phone</Heading>
                    <Value>{client?.phone ?? '--'}</Value>
                  </Grid>

                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Address</Heading>
                    <Value>{client?.address ?? '--'}</Value>
                  </Grid>
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Email</Heading>
                    <Value>{client?.email ?? '--'}</Value>
                  </Grid>
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Upcoming Appointments</Heading>
                    <Value>
                      {data?.clientAppointment?.startTime &&
                      new Date(data?.clientAppointment?.startTime) > new Date()
                        ? formatDateTime(data?.clientAppointment?.startTime)
                        : '--'}
                    </Value>
                  </Grid>
                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Alerts/Flags</Heading>
                    <Value sx={{ color: 'red' }}>
                      {data?.clientAlerts?.message ?? '--'}
                    </Value>
                  </Grid>

                  <Grid item lg={4} md={6} sm={12}>
                    <Heading fontWeight="bold">Special Notes</Heading>
                    <Value>{data?.clientNotes?.value ?? '--'}</Value>
                  </Grid>
                </Grid>
              </Paper>
            </Paper>
            <Paper
              elevation={1}
              sx={{
                mt: 2,
                padding: 2,
                width: '100%',
                boxShadow: 'none',
                borderRadius: '20px !important',
                backgroundColor: isDarkTheme ? 'rgb(41,41,41)' : '#F3F6FC',
                border: isDarkTheme
                  ? '1px solid rgb(41,41,41)'
                  : '1px solid #F3F6FC',
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, pl: 1, color: isDarkTheme ? '#ffffff' : '' }}
              >
                Pets Information
              </Typography>
              <Box
                sx={{
                  pr: petsScrollable ? 1 : 0,
                  overflowY: petsScrollable
                    ? { xs: 'auto', sm: 'auto', md: 'auto', lg: 'auto' }
                    : 'visible',
                  maxHeight: petsScrollable
                    ? {
                        xs: 650,
                        sm: 650,
                        md: 380,
                        lg: 300,
                      }
                    : 'none',
                }}
              >
                {pets?.map((pet: any, index: any) => (
                  <Paper
                    elevation={1}
                    key={
                      pet.bitwerx_patient_id || pet.vetspire_patient_id || index
                    }
                    sx={{
                      padding: 2,
                      width: '100%',
                      boxShadow: 'none',
                      border: '1px #e6e6e6',
                      marginTop: index === 0 ? 0 : 2,
                      borderRadius: '20px !important',
                      backgroundColor: isDarkTheme ? '#3C3C3C' : '#ffffff',
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      sx={{ color: isDarkTheme ? '#ffffff' : '' }}
                    >
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading>Pet Name</Heading>
                        <Value>{pet?.name ?? '--'}</Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Species</Heading>
                        <Value>{pet?.species ?? '--'}</Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Breed</Heading>
                        <Value>{pet?.breed ?? '--'}</Value>
                      </Grid>

                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Gender</Heading>
                        <Value>{pet?.gender ?? '--'}</Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Age</Heading>
                        <Value>
                          {pet?.date_of_birth
                            ? getFormattedAge(pet?.date_of_birth)
                            : '--'}
                        </Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Last weight taken</Heading>
                        <Value>{data?.petDetails?.vital?.value ?? '--'}</Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Last Visit Date</Heading>
                        <Value>
                          {data?.petDetails?.visits?.checkIn
                            ? formatDateTime(data?.petDetails?.visits?.checkIn)
                            : '--'}
                        </Value>
                      </Grid>

                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Appearance</Heading>
                        <Value>{data?.petDetails?.appearance ?? '--'}</Value>
                      </Grid>
                      <Grid item lg={4} md={6} sm={12}>
                        <Heading fontWeight="bold">Deceased</Heading>
                        <Value>
                          {data?.petDetails?.isDeceased
                            ? data?.petDetails?.isDeceased === true
                              ? 'Yes'
                              : 'No'
                            : '--'}
                        </Value>
                      </Grid>
                    </Grid>
                    {pets.length > 1 && (
                      <Grid item md={12} xs={12}>
                        <StandardButton
                          text="View Other Detail"
                          style={{
                            marginTop: '10px',
                          }}
                          onClick={() => {
                            setPetName(pet.name);
                            if (bitwerxStatus) {
                              setSelectedPetId(pet.bitwerx_patient_id);
                            } else if (vetsPireStatus) {
                              setSelectedPetId(pet.vetspire_patient_id);
                            }
                          }}
                        />
                      </Grid>
                    )}
                  </Paper>
                ))}
              </Box>
            </Paper>
          </Box>
        </Grid>
        <Grid
          item
          md={4}
          xs={12}
          sx={{
            height: {
              xs: 'auto',
              md: !petsScrollable ? '765px' : '850px',
              lg: !petsScrollable ? '600px' : '705px',
              xl: !petsScrollable ? '580px' : '687px',
            },
          }}
        >
          <Paper
            elevation={1}
            sx={{
              padding: 2,
              width: '100%',
              height: '100%',
              boxShadow: 'none',
              borderRadius: '20px !important',
              backgroundColor: isDarkTheme ? 'rgb(41,41,41)' : '#F3F6FC',
              border: isDarkTheme
                ? '1px solid rgb(41,41,41)'
                : '1px solid #F3F6FC',
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ pl: 1, mb: 0.8, color: isDarkTheme ? '#ffffff' : '' }}
            >
              Summary View
            </Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ mb: 2, pl: 1, color: isDarkTheme ? '#ffffff' : '' }}
            >
              Pet Name: {petName}
            </Typography>
            <Box
              sx={{
                height: '100%',
                maxHeight: '100%',
                overflowY: 'auto',
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  padding: 2,
                  width: '100%',
                  display: 'flex',
                  boxShadow: 'none',
                  border: '1px #e6e6e6',
                  flexDirection: 'column',
                  borderRadius: '20px !important',
                  backgroundColor: isDarkTheme ? '#3C3C3C' : '#ffffff',
                  height: { lg: '92%', md: '89%', sm: 'auto', xs: 'auto' },
                }}
              >
                <Grid
                  container
                  spacing={2}
                  sx={{ color: isDarkTheme ? '#ffffff' : '' }}
                >
                  <Grid item xl={6} lg={12} md={12} sm={12}>
                    <Heading>Last Appointment</Heading>
                    <Value>
                      {data?.petDetails?.appointment?.startTime
                        ? formatDateTime(
                            data?.petDetails?.appointment?.startTime
                          )
                        : '--'}
                    </Value>
                  </Grid>
                  <Grid item xl={6} lg={12} md={12} sm={12}>
                    <Heading fontWeight="bold">Last Prescription Date</Heading>
                    <Value>
                      {data?.petDetails?.prescriptions?.issuedDate
                        ? formatDateTime(
                            data?.petDetails?.prescriptions?.issuedDate
                          )
                        : '--'}
                    </Value>
                  </Grid>
                  <Grid item xl={6} lg={12} md={12} sm={12}>
                    <Heading fontWeight="bold">Reason for Last Visit</Heading>
                    <Value>{data?.petDetails?.visits?.reason ?? '--'}</Value>
                  </Grid>

                  <Grid item xl={6} lg={12} md={12} sm={12}>
                    <Heading fontWeight="bold">Prescriptions</Heading>
                    <Value>
                      {data?.petDetails?.prescriptions?.description ?? '--'}
                    </Value>
                  </Grid>
                  <Grid item xl={6} lg={12} md={12} sm={12}>
                    <Heading fontWeight="bold">Medical Diagnoses</Heading>
                    <Value>
                      {data?.petDetails?.diagnosis?.description ?? '--'}
                    </Value>
                  </Grid>
                  {data?.petDetails?.vaccinations?.map(
                    (value: any, index: any) => {
                      if (index < 10) {
                        return (
                          <Grid item xl={6} lg={12} md={12} sm={12}>
                            <Heading fontWeight="bold">
                              {value?.vaccinationType}
                            </Heading>
                            <Value>
                              {value?.issuedDate
                                ? formatDateTime(value?.issuedDate)
                                : '--'}
                            </Value>
                          </Grid>
                        );
                      }
                    }
                  )}
                  {data?.petDetails?.labResult?.map(
                    (value: any, index: any) => {
                      if (index < 10) {
                        return (
                          <Grid item xl={6} lg={12} md={12} sm={12}>
                            <Heading fontWeight="bold">{value?.name}</Heading>
                            <Value>{value?.result ?? '--'}</Value>
                          </Grid>
                        );
                      }
                    }
                  )}
                  {(() => {
                    const selectedPet = pets.find(
                      (pet: any) =>
                        (bitwerxStatus &&
                          pet.bitwerx_patient_id === selectedPetId) ||
                        (vetsPireStatus &&
                          pet.vetspire_patient_id === selectedPetId)
                    );
                    const protocols =
                      selectedPet?.protocols &&
                      Array.isArray(selectedPet.protocols)
                        ? [...selectedPet.protocols].sort((a: any, b: any) => {
                            const nameA = (
                              a?.protocol?.name ||
                              a?.name ||
                              ''
                            ).toLowerCase();
                            const nameB = (
                              b?.protocol?.name ||
                              b?.name ||
                              ''
                            ).toLowerCase();
                            return nameA.localeCompare(nameB);
                          })
                        : [];

                    if (protocols.length > 0) {
                      return (
                        <>
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            lg={12}
                            xl={12}
                            sx={{
                              mt: { xs: 2, md: 2, sm: 2, lg: 2 },
                              pt: { xs: 2, md: 2, sm: 2, lg: 2 },
                              borderTop: isDarkTheme
                                ? '1px solid rgba(255, 255, 255, 0.1)'
                                : '1px solid #e6e6e6',
                            }}
                          />
                          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              sx={{
                                // pl: 1,
                                mb: 1,
                                color: isDarkTheme ? '#ffffff' : '',
                              }}
                            >
                              Patient Reminders
                            </Typography>
                          </Grid>
                          {protocols.map((protocol: any, index: number) => {
                            const dueDate = protocol?.dueDate;
                            const isPastDate = dueDate
                              ? new Date(dueDate) < new Date()
                              : false;
                            const backgroundColor = isPastDate
                              ? 'rgba(255, 0, 0, 0.1)'
                              : 'rgba(0, 255, 0, 0.1)';
                            const textColor = isPastDate
                              ? '#d32f2f'
                              : '#2e7d32';

                            return (
                              <Grid
                                item
                                xl={12}
                                lg={12}
                                md={12}
                                sm={12}
                                xs={12}
                                key={protocol?.id || index}
                                sx={{
                                  paddingTop: '4px !important',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: '100%',
                                    display: 'flex',
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom:
                                      index < protocols.length - 1 ? '4px' : 0,
                                    backgroundColor: isDarkTheme
                                      ? isPastDate
                                        ? 'rgba(255, 0, 0, 0.15)'
                                        : 'rgba(0, 255, 0, 0.15)'
                                      : backgroundColor,
                                  }}
                                >
                                  <Heading
                                    fontWeight="bold"
                                    sx={{
                                      color: isDarkTheme
                                        ? '#ffffff'
                                        : textColor,
                                    }}
                                  >
                                    {protocol?.protocol?.name ||
                                      protocol?.name ||
                                      '--'}
                                  </Heading>
                                  {dueDate && (
                                    <Value
                                      sx={{
                                        fontWeight: 500,
                                        color: isDarkTheme
                                          ? '#ffffff'
                                          : textColor,
                                        fontSize: {
                                          xs: '0.75rem',
                                          md: '0.875rem',
                                          lg: '0.875rem',
                                          sm: '0.8125rem',
                                        },
                                      }}
                                    >
                                      {formatDateTime(dueDate)}
                                    </Value>
                                  )}
                                </Box>
                              </Grid>
                            );
                          })}
                        </>
                      );
                    }
                    return null;
                  })()}
                </Grid>
              </Paper>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
